import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material'
import { POProgressBar } from './POProgressBar'
import type { PurchaseOrderWithUsage } from '@/types/po'

interface POTableProps {
  pos: PurchaseOrderWithUsage[]
  loading: boolean
  onEdit: (po: PurchaseOrderWithUsage) => void
  onDelete: (id: string) => void
  onFilter: (poId: string | null) => void
  filteredPOId: string | null
}

export function POTable({ pos, loading, onEdit, onDelete, onFilter, filteredPOId }: POTableProps) {
  const formatEUR = (val: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val)

  if (loading) {
    return (
      <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    )
  }

  if (pos.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          No POs found for this year
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Create a new PO to get started
        </Typography>
      </Paper>
    )
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>PO Number</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">Total Budget</TableCell>
            <TableCell>Usage</TableCell>
            <TableCell align="right">Used</TableCell>
            <TableCell align="right">Remaining</TableCell>
            <TableCell align="center">Forecast</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pos.map((po) => (
            <TableRow
              key={po.id}
              hover
              sx={{
                backgroundColor: filteredPOId === po.id ? 'action.selected' : 'inherit',
              }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {po.poNumber}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="textSecondary">
                  {po.description || '-'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">{formatEUR(po.valueTotalEur)}</Typography>
              </TableCell>
              <TableCell sx={{ minWidth: 250 }}>
                <POProgressBar valueUsed={po.valueUsed} valueTotal={po.valueTotalEur} />
              </TableCell>
              <TableCell align="right">
                <Typography
                  variant="body2"
                  color={po.isOverrun ? 'error' : 'textPrimary'}
                  fontWeight={po.isOverrun ? 500 : 400}
                >
                  {formatEUR(po.valueUsed)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography
                  variant="body2"
                  color={po.valueRemaining < 0 ? 'error' : 'textSecondary'}
                >
                  {formatEUR(Math.max(0, po.valueRemaining))}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Button
                  size="small"
                  variant={filteredPOId === po.id ? 'contained' : 'outlined'}
                  startIcon={filteredPOId === po.id ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  onClick={() => onFilter(filteredPOId === po.id ? null : po.id)}
                >
                  {filteredPOId === po.id ? 'Clear' : 'Filter'}
                </Button>
              </TableCell>
              <TableCell align="center">
                <IconButton size="small" onClick={() => onEdit(po)} color="primary">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(po.id)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
